import { toKebabTitleCase } from '../../../utils';
import { visualizations as vizMappings } from '../../Visualization';

const TOOLBAR_ITEMS = [
  { key: 'signalViewerTools', label: 'Signal viewer tools' },
  { key: 'datetimepicker', label: 'Date time picker' },
  { key: 'tags', label: 'Tags' },
  { key: 'search', label: 'Search' },
];

const createMultiSelect = (title, options = [], extra = {}) => ({
  type: 'array',
  title,
  default: [],
  uniqueItems: true,
  isMulti: true,
  items: {
    type: 'string',
    anyOf: options,
  },
  ...extra,
});

const buildEnumOptions = (items, mapFunction) => items.map(mapFunction);

const getHiddenToolbarItemsField = (hideTagsItem = false) => {
  const items = hideTagsItem ? TOOLBAR_ITEMS.filter(({ key }) => key !== 'tags') : TOOLBAR_ITEMS;
  return createMultiSelect(
    'Hide toolbar items',
    items.map(({ key, label }) => ({
      enum: [key],
      title: label,
    })),
    {
      description: 'Select which parts of the toolbar to hide',
    }
  );
};

const withToolbarDependency = (properties, hideTagsItem = false) => ({
  ...properties,
  dependencies: {
    hideToolbar: {
      oneOf: [
        { properties: { hideToolbar: { enum: [true] } } },
        {
          properties: {
            hideToolbar: { enum: [false] },
            hiddenToolbarItems: getHiddenToolbarItemsField(hideTagsItem),
          },
        },
      ],
    },
  },
});

const getVisualizationTagOptions = (tags = []) =>
  tags
    ?.filter((tag) => tag.categories?.includes('visualizations'))
    .map((tag) => ({
      enum: [tag.id],
      title: tag.name,
    })) || [];

const getVisualizationOptions = (visualizations = []) =>
  buildEnumOptions(visualizations, (viz) => ({
    enum: [viz.name],
    title: `${viz.name} (${viz.type})`,
  }));

const getVisualizationTypeOptions = () =>
  buildEnumOptions(vizMappings, ({ type }) => ({
    enum: [type],
    title: type === 'duval' ? 'Duval-Triangle' : toKebabTitleCase(type),
  }));

const getBaseOptions = () => ({
  autoPopulate: {
    title: 'Auto-populate with visualizations related to selected component',
    type: 'boolean',
    default: false,
  },
  hideToolbar: {
    title: 'Hide toolbar',
    type: 'boolean',
    default: true,
  },
  syncVisualizationTooltip: {
    title: 'Sync the visualization tooltip',
    type: 'boolean',
    default: false,
  },
  visualizationLimit: {
    title: 'Limit visualizations per panel',
    type: 'number',
    default: 10,
  },
});

const getVisualizationsPanelSchema = (
  visualizations = [],
  tags = [],
  tabGroups = [],
  showTabs = true
) => {
  const hideTagsItem = tabGroups?.length >= 2 && showTabs !== false;
  const visualizationTagOptions = getVisualizationTagOptions(tags);

  const properties = {
    ...getBaseOptions(),

    height: {
      type: 'string',
      title: 'Visualization height',
      enum: ['extra-small', 'small', 'medium-small', 'medium', 'large'],
      default: 'medium',
      extraText: {
        'extra-small':
          'Extra-small height will only display metric, state and status visualizations',
      },
      extraTextTriggers: ['extra-small'],
    },

    width: {
      type: 'string',
      title: 'Visualization width',
      enum: ['small', 'medium-small', 'medium', 'large'],
      default: 'medium',
    },

    visualizationTypes: createMultiSelect('Visualization Types', getVisualizationTypeOptions(), {
      description:
        'Limit auto-populated visualizations to the selected types (no selected types means that all types are allowed)',
    }),
  };

  if (visualizations.length > 0) {
    properties.visualizations = createMultiSelect(
      'Visualizations',
      getVisualizationOptions(visualizations)
    );
  }

  if (visualizationTagOptions.length > 0) {
    properties.tabGroups = {
      type: 'array',
      title: 'Tab Groups',
      description: 'Tabs will only be visible when at least 2 tabs are configured',
      default: [],
      maxItems: 5,
      items: {
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Tab Name', minLength: 1 },
          tags: createMultiSelect('Filter by Tags', visualizationTagOptions, {
            minItems: 1,
          }),
        },
        required: ['name', 'tags'],
      },
    };
  }

  if (tabGroups?.length >= 2) {
    properties.showTabs = {
      title: 'Show tabs (uncheck to use tag filter instead)',
      type: 'boolean',
      default: true,
      description: 'Toggle between tabs view and tag filter view.',
    };
  }

  return {
    title: {
      type: 'string',
      title: 'Title',
      default: 'Visualizations',
    },
    options: {
      type: 'object',
      properties,
      ...withToolbarDependency({}, hideTagsItem),
    },
  };
};

const getVisualizationsPanelAdminSchema = (
  visualizations = [],
  isExtraSmallHeight = false,
  tabGroups = [],
  showTabs = true
) => {
  const hideTagsItem = tabGroups?.length >= 2 && showTabs !== false;
  const properties = {
    ...getBaseOptions(),

    visualizations:
      visualizations.length > 0
        ? createMultiSelect(
            'Include specific visualizations',
            getVisualizationOptions(visualizations),
            isExtraSmallHeight
              ? {
                  extraText: {
                    'extra-small': 'Only metric, state, and status visualizations visible.',
                  },
                  extraTextKey: 'extra-small',
                }
              : {}
          )
        : {
            type: 'object',
            title: 'Visualizations',
            description: 'None available',
            properties: {},
          },
  };

  return {
    type: 'object',
    properties,
    ...withToolbarDependency({}, hideTagsItem),
  };
};

export { getVisualizationsPanelSchema, getVisualizationsPanelAdminSchema };

// Controls rendering order so hiddenToolbarItems always appears
// immediately below hideToolbar when the dependency shows it.
export const visualizationsPanelOptionsUiSchema = {
  'ui:order': [
    'autoPopulate',
    'hideToolbar',
    'hiddenToolbarItems',
    'syncVisualizationTooltip',
    'visualizationLimit',
    '*',
  ],
};
